import {
  PlaytestRecruitmentConfigRowSchema,
  type PlaytestRecruitmentConfig,
  type PlaytestRecruitmentConfigInput,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Playtest recruitment-config data layer.
//
// One row per game; reads via PostgREST (RLS scopes by studio), writes
// via the security-definer RPC in migration 0053. The bot side (PR 2)
// updates `posted_message_ids` / `last_posted_at` via the service role
// after each post; the portal only reads those columns.
// ---------------------------------------------------------------------------

const TABLE = 'playtest_recruitment_configs';

function parse(raw: unknown): PlaytestRecruitmentConfig | null {
  const parsed = PlaytestRecruitmentConfigRowSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('[recruitment] Could not parse config row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

export async function fetchPlaytestRecruitmentConfig(
  gameId: string,
): Promise<PlaytestRecruitmentConfig | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('game_id', gameId)
    .maybeSingle();
  if (error !== null) {
    console.warn('[recruitment] Failed to load config:', error.message);
    return null;
  }
  if (data === null) return null;
  return parse(data);
}

// RPC error tokens raised by 0053. The portal maps these to user-facing
// copy at the call site.
export type PlaytestRecruitmentRpcError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'game_not_found'
  | 'game_id_required'
  | 'title_required'
  | 'title_too_long'
  | 'description_required'
  | 'description_too_long'
  | 'button_label_required'
  | 'button_label_too_long'
  | 'invalid_repost_frequency'
  | 'channel_ids_required'
  | 'too_many_channels'
  | 'pairing_instructions_required'
  | 'pairing_instructions_too_long'
  | 'enabled_required'
  | 'recruitment_not_configured'
  | 'recruitment_not_enabled'
  | 'recruitment_cooldown'
  | 'unknown';

export interface PlaytestRecruitmentRpcResult<T> {
  ok: boolean;
  data: T | null;
  error: PlaytestRecruitmentRpcError | null;
}

const KNOWN_ERROR_TOKENS = new Set<PlaytestRecruitmentRpcError>([
  'not_authenticated',
  'not_a_member',
  'game_not_found',
  'game_id_required',
  'title_required',
  'title_too_long',
  'description_required',
  'description_too_long',
  'button_label_required',
  'button_label_too_long',
  'invalid_repost_frequency',
  'channel_ids_required',
  'too_many_channels',
  'pairing_instructions_required',
  'pairing_instructions_too_long',
  'enabled_required',
  'recruitment_not_configured',
  'recruitment_not_enabled',
  'recruitment_cooldown',
]);

function classifyError(message: string | undefined | null): PlaytestRecruitmentRpcError {
  if (typeof message !== 'string') return 'unknown';
  for (const token of KNOWN_ERROR_TOKENS) {
    if (message.includes(token)) return token;
  }
  return 'unknown';
}

export async function upsertPlaytestRecruitmentConfig(
  gameId: string,
  input: PlaytestRecruitmentConfigInput,
): Promise<PlaytestRecruitmentRpcResult<PlaytestRecruitmentConfig>> {
  const { data, error } = await supabase.rpc('upsert_playtest_recruitment_config', {
    p_game_id: gameId,
    p_title: input.title,
    p_description: input.description,
    p_button_label: input.buttonLabel,
    p_repost_frequency: input.repostFrequency,
    p_channel_ids: input.channelIds,
    p_pairing_instructions: input.pairingInstructions,
    p_enabled: input.enabled,
  });
  if (error !== null) {
    console.warn('[recruitment] upsert failed:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
    });
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parse(data);
  if (parsed === null) return { ok: false, data: null, error: 'unknown' };
  return { ok: true, data: parsed, error: null };
}

export async function requestRecruitmentRepost(
  gameId: string,
): Promise<PlaytestRecruitmentRpcResult<null>> {
  const { error } = await supabase.rpc('request_recruitment_repost', {
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn('[recruitment] repost request failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  return { ok: true, data: null, error: null };
}
